import { useEffect, useState } from "react";
import { Video, Play, X, Download, RefreshCw, Clock } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Admin view for stored video recordings.
 * Each recording's chunks are stitched in the browser via Blob concatenation.
 */
export function AdminRecordingsTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [player, setPlayer] = useState(null);
    const [stitching, setStitching] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/recordings");
            setItems(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load recordings");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const open = async (rec) => {
        setStitching(true);
        try {
            const { data } = await api.get(`/admin/recordings/${rec.id}/chunks`);
            const blobs = [];
            let badChunks = 0;
            for (const c of (data.chunks || [])) {
                try {
                    const b64 = (c.data_url || "").split(",")[1] || "";
                    if (!b64) { badChunks++; continue; }
                    const bin = atob(b64);
                    const u8 = new Uint8Array(bin.length);
                    for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
                    blobs.push(new Blob([u8], { type: c.mime_type || "video/webm" }));
                } catch {
                    badChunks++;
                }
            }
            if (blobs.length === 0) {
                toast.error("No playable chunks in this recording");
                return;
            }
            if (badChunks > 0) {
                toast.warning(`${badChunks} chunk(s) skipped (corrupt); playing the rest.`);
            }
            const blob = new Blob(blobs, { type: blobs[0].type });
            const url = URL.createObjectURL(blob);
            setPlayer({ rec: data.recording, url, blob });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load recording");
        } finally {
            setStitching(false);
        }
    };

    const close = () => {
        if (player?.url) URL.revokeObjectURL(player.url);
        setPlayer(null);
    };

    const download = () => {
        if (!player) return;
        const a = document.createElement("a");
        a.href = player.url;
        a.download = `medxai-recording-${player.rec.id}.webm`;
        a.click();
    };

    return (
        <div className="space-y-6" data-testid="admin-tab-recordings">
            <div className="flex items-center justify-between">
                <h2 className="font-display text-2xl font-semibold flex items-center gap-2">
                    <Video className="w-6 h-6 text-[var(--medx-red)]" /> Video Recordings
                </h2>
                <button onClick={load} className="btn-outline !py-2 !px-4 text-sm inline-flex items-center gap-2" data-testid="admin-recordings-refresh">
                    <RefreshCw className="w-4 h-4" /> Refresh
                </button>
            </div>

            {loading ? (
                <div className="text-center py-12 text-gray-500">Loading…</div>
            ) : items.length === 0 ? (
                <div className="card-soft p-12 text-center text-sm text-gray-500">
                    <Video className="w-8 h-8 mx-auto text-[var(--medx-orange)] mb-3" />
                    No recordings yet. Recordings are saved automatically when a patient consents to a recorded video consultation.
                </div>
            ) : (
                <div className="card-soft overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-500 text-xs font-mono tracking-wider">
                            <tr>
                                <th className="text-left p-4">PARTICIPANT</th>
                                <th className="text-left p-4">CONSULTATION</th>
                                <th className="text-left p-4">DURATION</th>
                                <th className="text-left p-4">CHUNKS</th>
                                <th className="text-left p-4">RECORDED</th>
                                <th className="text-right p-4">ACTIONS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((r) => (
                                <tr key={r.id} className="border-t border-gray-100 hover:bg-orange-50/40" data-testid={`admin-recording-row-${r.id}`}>
                                    <td className="p-4">
                                        <div className="font-medium">{r.uploaded_by_name || r.uploaded_by_role}</div>
                                        <div className="text-xs text-gray-500">{r.uploaded_by_role}</div>
                                    </td>
                                    <td className="p-4">
                                        <div className="text-xs text-gray-700">{r.doctor_name} ↔ {r.patient_name}</div>
                                        <div className="text-[10px] font-mono text-gray-400 truncate max-w-[200px]">{r.consultation_id}</div>
                                    </td>
                                    <td className="p-4 flex items-center gap-1 text-gray-700"><Clock className="w-3 h-3" /> {r.duration_seconds}s</td>
                                    <td className="p-4 text-gray-700">{r.live_chunk_count}</td>
                                    <td className="p-4 text-xs text-gray-500">{new Date(r.created_at).toLocaleString()}</td>
                                    <td className="p-4 text-right">
                                        <button onClick={() => open(r)} disabled={stitching} className="btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5" data-testid={`admin-recording-play-${r.id}`}>
                                            <Play className="w-3 h-3" /> Play
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {player && (
                <div className="fixed inset-0 z-[70] bg-black/90 grid place-items-center p-4" data-testid="admin-recording-player">
                    <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center" onClick={close} data-testid="admin-recording-close">
                        <X className="w-5 h-5" />
                    </button>
                    <div className="max-w-4xl w-full">
                        <div className="text-white text-sm mb-3 flex items-center justify-between">
                            <div>
                                <div className="font-display font-semibold">{player.rec.doctor_name} ↔ {player.rec.patient_name}</div>
                                <div className="text-xs text-gray-400 font-mono">{player.rec.consultation_id}</div>
                            </div>
                            <button onClick={download} className="btn-outline text-xs !py-1.5 !px-3 text-white border-white/30 inline-flex items-center gap-1.5">
                                <Download className="w-3 h-3" /> Download
                            </button>
                        </div>
                        <video src={player.url} controls autoPlay className="w-full rounded-2xl shadow-2xl bg-black" data-testid="admin-recording-video" />
                    </div>
                </div>
            )}
        </div>
    );
}
